import { useEffect, useState, useRef, useCallback } from "react";
import { X, Send, Loader2 } from "lucide-react";
import api from "../lib/api";
import { realtime } from "../lib/ws";
import { Avatar } from "../lib/helpers";
import { useAuth } from "../context/AuthContext";

export const BoardChat = ({ boardId, onClose }) => {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/boards/${boardId}/messages`);
      setMsgs(data);
    } catch {}
  }, [boardId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const off = realtime.on((msg) => {
      if (msg.type === "chat_message" && msg.message?.board_id === boardId) {
        setMsgs((m) => ((m || []).some((x) => x.id === msg.message.id) ? m : [...(m || []), msg.message]));
      }
    });
    return () => off();
  }, [boardId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/boards/${boardId}/messages`, { text });
      setMsgs((m) => ((m || []).some((x) => x.id === data.id) ? m : [...(m || []), data]));
      setText("");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[380px] bg-[#0E0E10] border-l border-white/10 flex flex-col shadow-2xl animate-slide-in-right" data-testid="board-chat">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/10">
        <h3 className="font-display font-bold">Chat del tablero</h3>
        <button onClick={onClose} data-testid="close-chat"><X className="h-5 w-5 text-zinc-500 hover:text-white" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {msgs === null && <p className="text-sm text-zinc-600">Cargando…</p>}
        {msgs?.length === 0 && <p className="text-sm text-zinc-600 text-center mt-8">Empieza la conversación del equipo.</p>}
        {(msgs || []).map((m, mi) => {
          const mine = m.user_id === user?.id;
          const system = m.user_id === "system";
          if (system) {
            return (
              <div key={m.id} className="text-center animate-fade-up" style={{ animationDelay: `${Math.min(mi * 30, 300)}ms` }} data-testid={`chat-msg-${m.id}`}>
                <p className="inline-block text-xs text-zinc-400 bg-white/5 rounded-full px-3 py-1.5 whitespace-pre-wrap text-left max-w-[90%]">{m.text}</p>
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex gap-2.5 animate-fade-up ${mine ? "flex-row-reverse" : ""}`} style={{ animationDelay: `${Math.min(mi * 30, 300)}ms` }} data-testid={`chat-msg-${m.id}`}>
              <Avatar user={m.user} size={28} />
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${mine ? "bg-primary/20 rounded-tr-sm" : "bg-[#1C1C1F] rounded-tl-sm"}`}>
                {!mine && <p className="text-[11px] font-semibold text-zinc-400">{m.user?.name}</p>}
                <p className="text-sm text-zinc-100 whitespace-pre-wrap break-words">{m.text}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5 text-right">{new Date(m.created_at).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe un mensaje…"
          className="flex-1 bg-[#1C1C1F] border border-white/10 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary" />
        <button type="submit" disabled={sending} data-testid="chat-send" className="bg-primary hover:bg-[#FF5047] rounded-xl px-3.5 flex items-center disabled:opacity-60">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
};
